import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, LifeBuoy, Plus, Receipt, Search, Users } from 'lucide-react';
import {
  ArchivedToggle,
  Button,
  Card,
  DataTable,
  EmptyState,
  ExportCsvButton,
  InfoHint,
  PageHeader,
  SearchInput,
  SkeletonTable,
  StatusPill,
  parseSortParam,
  serializeSortParam,
  sortRows,
  type DataTableColumn,
} from '../../components/ui';
import { csvFilename, csvHours, csvMoney, csvPercent, downloadCsv } from '../../lib/csv';
import { cn } from '../../lib/cn';
import { formatDate, formatHours, formatMoney } from '../../lib/format';
import { MarginText } from '../agreements/components';
import { bankStatusLabel } from '../support/components';
import { useClients, type ClientListRow } from './api';
import { bankSignal } from './support-signal';
// One definition of this money, and one link to act on it — shared with the
// client detail page so the two screens cannot describe it differently.
import {
  PREPAID_TITLE,
  UNBILLED_SPLIT_TITLE,
  UNBILLED_TITLE,
  unbilledSplit,
  unbilledWizardHref,
} from './unbilled';
import { REVENUE_BASIS_NOTE_SHORT, RevenueBasisInfo } from './revenue-basis';
// "New client" opens on the Accounts organizations first (see ClientFormModal).
import { NewClientModal } from './components/NewClientModal';

const YEAR = new Date().getFullYear();

// ⚠ NOT the firm's revenue. The app recognises revenue on a CASH basis (money
// counts when it arrives — backend `pnl/revenue.ts`); this column values the
// WORK, which is a different question with three different answers depending on
// how the engagement is priced. The sentence is shared with the client PAGE
// (`revenue-basis.ts`) so the list and the page it opens cannot drift — which
// they had: this tooltip named cash and the P&L while the detail tile was still
// headed "Revenue earned" with a tooltip that named neither.
/** The extra clause this LIST adds to the shared revenue-basis explanation. */
const REVENUE_SCOPE_NOTE = `All time. It is exactly the Revenue column on /agreements, grouped by client, so the two lists total the same. The second line under each figure is how much of the relationship is ${YEAR}.`;
const YTD_TITLE = `Invoiced so far in ${YEAR} — accrual, by issue date, excluding drafts and voids. A third basis again: not the work's value above it, and not the firm's cash revenue on the Profit & Loss. That is why it is a hint and not a second column pretending to be comparable — and why the word "billed" rides on it at every width.`;
const PROJECTS_TITLE =
  'Agreements with work still running. A trailing number is how many more are archived — a client with none active is dormant, not agreementless.';
const COST_TITLE =
  'Delivery cost across every agreement of this client, archived included: tracked time at each person’s cost rate plus agreement expenses.';
export function ClientsPage() {
  const navigate = useNavigate();
  // Filters live in the URL, exactly like /agreements, /invoices and /team — a
  // view of this list is a link you can share and the back button restores.
  const [params, setParams] = useSearchParams();
  // "Show archived" is the counted toggle every other list uses, not a
  // three-way Active/Archived/All strip. The query always asks for `all` so
  // the count is free and the toggle is additive, the way /agreements and /team
  // already behaved.
  const showArchived = params.get('archived') === '1';
  const search = params.get('q') ?? '';
  const patch = useCallback(
    (next: Record<string, string | null>) => {
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(next)) {
            if (value == null || value === '') out.delete(key);
            else out.set(key, value);
          }
          return out;
        },
        { replace: true },
      );
    },
    [setParams],
  );
  const setSearch = (next: string) => patch({ q: next || null });
  // The sort lives in the URL with the filters, so a "worst margin first" view
  // is a link — and so the CSV can be exported in exactly the order on screen.
  const sort = useMemo(
    () => parseSortParam(params.get('sort'), { key: 'name', dir: 'asc' }),
    [params],
  );
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useClients('all', search);
  const allClients = data?.clients ?? [];
  const archivedCount = allClients.filter((c) => !c.isActive).length;
  const clients = showArchived ? allClients : allClients.filter((c) => c.isActive);

  /**
   * The same rollup the /agreements footer prints, one level up: client work and
   * internal overhead totalled separately so a blended margin never quietly
   * absorbs non-billable cost.
   */
  const totals = useMemo(() => {
    const t = {
      count: clients.length,
      hours: 0,
      revenue: 0,
      ytdRevenue: 0,
      cost: 0,
      internalCost: 0,
      unbilled: 0,
      unbilledPrepaid: 0,
      unbilledPrepaidHours: 0,
      openAr: 0,
      overdueAr: 0,
      supportBalance: 0,
      supportOverage: 0,
    };
    for (const c of clients) {
      t.hours += c.hours;
      t.revenue += c.revenue;
      t.ytdRevenue += c.ytdRevenue;
      t.cost += c.cost;
      t.internalCost += c.internalCost;
      t.unbilled += c.unbilled;
      t.unbilledPrepaid += c.unbilledPrepaid;
      t.unbilledPrepaidHours += c.unbilledPrepaidHours;
      t.openAr += c.openAr;
      t.overdueAr += c.overdueAr;
      if (c.support) {
        t.supportBalance += c.support.balance;
        t.supportOverage += c.support.overage;
      }
    }
    return { ...t, margin: t.revenue > 0 ? (t.revenue - t.cost) / t.revenue : null };
  }, [clients]);

  const columns: Array<DataTableColumn<ClientListRow>> = [
    {
      key: 'name',
      header: 'Client',
      sortable: true,
      sortValue: (c) => c.name,
      render: (c) => {
        // The prepaid support bank, when it has something to say. It is a
        // property of the CLIENT, so it rides in the client cell rather than
        // buying a ninth column this table has no width for (see the note on
        // the agreements column) — and it therefore survives 1024px and the
        // phone, which is where a $4,163 evaporation was least visible.
        //
        // ⚠ Plain text, not a link: DataTable wraps the FIRST cell in the
        // row's own <Link>, and an anchor inside an anchor is invalid.
        const support = bankSignal(c.support, c.support?.rate);
        return (
          <div className="min-w-[160px]">
            <span className="font-semibold text-secondary">{c.name}</span>
            {!c.isActive && <StatusPill status="neutral" label="Archived" dot={false} className="ml-2" />}
            {support && (
              <span
                className={cn(
                  'mt-0.5 flex items-center gap-1 text-caption font-semibold',
                  support.tone === 'bad' ? 'text-status-late' : 'text-warning',
                )}
                title={support.title}
              >
                <LifeBuoy className="h-3 w-3 shrink-0" aria-hidden />
                <span className="num truncate">{support.text}</span>
              </span>
            )}
            {c.notes && (
              // The cap on a `truncate` cell IS this column's minimum width (see
              // CLAUDE.md) — at `max-w-xs` one client note reserved 320px and
              // pushed the money columns off the right edge of the table's own
              // scroll box, silently, at 1024 and 1280. 11rem, with the full note
              // on hover.
              <p className="mt-0.5 max-w-[11rem] truncate text-caption text-grey-500" title={c.notes}>
                {c.notes}
              </p>
            )}
            {/* Revenue and AR are the columns a phone scrolls off the right edge,
                and Margin is the one figure this app exists to show. Below `sm`
                the row keeps Client + Margin as real columns and restates revenue
                and the more urgent of open/overdue on one line under the name. */}
            <p className="num mt-0.5 text-caption sm:hidden">
              {/* ⚠ "rev" IS NOT A BASIS. The desktop row carries its basis in a
                  column header and a tooltip; below `sm` both are gone and this
                  line was the only revenue figure on screen, reading
                  "$250,000 rev" — which a reader will take for the firm's
                  revenue, a cash figure this is not. It says which it is at
                  every width now; the line already wraps at 390px. */}
              {c.revenue > 0 && (
                <span className="whitespace-nowrap text-grey-500">
                  {formatMoney(c.revenue, { whole: true })} work value
                </span>
              )}
              {c.revenue > 0 && (c.openAr > 0 || c.unbilled > 0) && (
                <span className="text-grey-300"> · </span>
              )}
              {c.overdueAr > 0 ? (
                <span className="whitespace-nowrap font-semibold text-primary-600">
                  {formatMoney(c.overdueAr, { whole: true })} overdue
                </span>
              ) : c.openAr > 0 ? (
                <span className="whitespace-nowrap text-grey-500">
                  {formatMoney(c.openAr, { whole: true })} open
                </span>
              ) : null}
              {/* Unbilled rides the phone summary too — "who have I not invoiced?"
                  is the question this list is opened with, and hiding it in a
                  column no phone renders is how it went missing in the first
                  place. It wraps rather than clipping at 390px. */}
              {c.unbilled > 0 && (
                <>
                  {(c.openAr > 0 || c.revenue > 0) && <span className="text-grey-300"> · </span>}
                  <span className="whitespace-nowrap font-semibold text-accent">
                    {formatMoney(c.unbilled, { whole: true })} to bill
                  </span>
                </>
              )}
              {/* ⚠ THE PREPAID FACT RIDES THE PHONE TOO. The desktop row prints
                  "14.00 h prepaid" under the Unbilled column and the phone
                  dropped it — so the one fact that stops you double-billing was
                  missing on the device where the money column is missing too.
                  On a fully-prepaid client it is the ONLY thing this line has
                  to say about their uninvoiced work. */}
              {c.unbilledPrepaid > 0 && (
                <>
                  {(c.openAr > 0 || c.revenue > 0 || c.unbilled > 0) && (
                    <span className="text-grey-300"> · </span>
                  )}
                  <span className="whitespace-nowrap text-grey-500" title={PREPAID_TITLE}>
                    {formatHours(c.unbilledPrepaidHours, { fixed: true })} h prepaid
                  </span>
                </>
              )}
            </p>
          </div>
        );
      },
    },
    {
      key: 'activeAgreements',
      header: <span title={PROJECTS_TITLE}>Active agreements</span>,
      align: 'right',
      sortable: true,
      sortValue: (c) => c.activeAgreements,
      // Column visibility is a budget, not a preference. The content column at
      // a 1024 viewport is ~720px once the rail and sidebar are paid for, and
      // eight columns do not fit in it — they were being sliced off inside the
      // table's own overflow box with no scrollbar and no edge shadow, so the
      // money columns simply did not exist for anyone on half a laptop screen.
      // The two that drop are the two that matter least on a triage pass:
      // agreement count, and cost (the margin beside it already encodes it).
      // Unbilled, open AR and overdue stay down to 768px. Everything survives
      // in the CSV, which is not responsive.
      //
      // Measured, with the 64px app rail in place: the eight columns need
      // 985px of min-content and a 1280 viewport gives the table 926px. So the
      // agreement count is the one that waits for 2xl — a client's liveness is
      // also readable from the "$X in 2026" line under its revenue.
      className: 'hidden 2xl:table-cell',
      // A client with two archived agreements and none live used to print a bare
      // "—" here while its own page said "2 agreements". Dormant is a state, not
      // an absence: the archived count is named rather than hidden.
      render: (c) =>
        c.activeAgreements > 0 ? (
          <span title={`${c.totalAgreements} on record, archived included`}>{c.activeAgreements}</span>
        ) : c.totalAgreements > 0 ? (
          <span
            className="text-grey-500"
            title={`Dormant — all ${c.totalAgreements} of this client's agreements are archived`}
          >
            0 <span className="text-caption text-grey-400">of {c.totalAgreements}</span>
          </span>
        ) : (
          <span className="text-grey-400">—</span>
        ),
    },
    {
      key: 'revenue',
      // ⚠ NO (i) IN A SORTABLE HEADER — `DataTable` wraps a sortable header in
      // a <button>, and an InfoHint is a <button>, so nesting them is invalid
      // DOM (React says so out loud) and gives the glyph two conflicting
      // keyboard meanings. The basis (i) lives on the totals band instead,
      // which is a plain <td>; the rollup is the figure it explains anyway.
      header: 'Revenue · all time',
      align: 'right',
      sortable: true,
      sortValue: (c) => c.revenue,
      className: 'hidden sm:table-cell',
      // Whole dollars in every money column on this screen. A row that reads
      // "$167,253" beside "$26,418.75" is two precisions in one glance; cents
      // belong to the invoice ledger, where someone pays an exact amount.
      //
      // The second line is the period scope this screen had none of: the header
      // said ALL TIME and nothing on the page said which year the money came
      // from, so a relationship that ended in 2019 outranked one billing hard
      // now. `ytdRevenue` was already in the payload and rendered nowhere.
      render: (c) =>
        c.revenue > 0 || c.ytdRevenue > 0 ? (
          <>
            <div>
              {c.revenue > 0 ? (
                formatMoney(c.revenue, { whole: true })
              ) : (
                <span className="text-grey-400">—</span>
              )}
            </div>
            <div
              className="text-caption font-normal leading-tight text-grey-500"
              title={YTD_TITLE}
            >
              {/* ⚠ THE BASIS WORD IS PART OF THE FIGURE, NOT OF THE TOOLTIP.
                  This read "$167,253 in 2026" — a number with no basis, one
                  line under a differently-based number, on a screen whose
                  header says the firm's revenue is something else again. */}
              {c.ytdRevenue > 0
                ? `${formatMoney(c.ytdRevenue, { whole: true })} billed in ${YEAR}`
                : `nothing billed in ${YEAR}`}
            </div>
          </>
        ) : (
          <span className="text-grey-400">—</span>
        ),
    },
    {
      key: 'cost',
      header: <span title={COST_TITLE}>Cost</span>,
      align: 'right',
      sortable: true,
      sortValue: (c) => c.cost,
      className: 'hidden xl:table-cell',
      render: (c) =>
        c.cost > 0 ? (
          formatMoney(c.cost, { whole: true })
        ) : c.internalCost > 0 ? (
          // An internal-only client (our own overhead) still costs real money.
          // Printing "—" would hide six figures of it; it is greyed and named
          // instead, and the footer totals it separately from client work.
          <span
            className="text-grey-500"
            title="Internal / non-billable cost — excluded from the margin column and from the client-work total."
          >
            {formatMoney(c.internalCost, { whole: true })}
          </span>
        ) : (
          <span className="text-grey-400">—</span>
        ),
    },
    {
      key: 'margin',
      header: 'Margin',
      align: 'right',
      sortable: true,
      // Null, not −999: a client with no revenue has no margin, and coercing it
      // to "worse than everything" put Solid Digital (Internal) at the top of
      // "worst margin first" — above the client actually losing money. Blanks
      // sink in both directions (see DataTable#sortRows).
      sortValue: (c) => c.margin,
      render: (c) => <MarginText margin={c.margin} />,
    },
    // "Who has work I haven't invoiced?" — the question an agency asks this
    // list most often, and the one it could not answer even though the figure
    // was already in the payload. It sits where the money sits in real life:
    // delivered → unbilled → open → overdue, left to right.
    {
      key: 'unbilled',
      header: <span title={UNBILLED_TITLE}>Unbilled</span>,
      align: 'right',
      sortable: true,
      sortValue: (c) => c.unbilled,
      className: 'hidden md:table-cell',
      // ⚠ A row whose whole unbilled figure was prepaid must still say what
      // happened to it. Netting the money and printing a bare dash makes
      // 21.50 h of delivered support vanish off the screen, which reads as
      // missing data rather than as work that is already paid for.
      render: (c) => {
        // ⚠ THE ADDITION IS ON SCREEN. The Unbilled figure is billable time
        // plus billable expenses, and on a support client in overage the time
        // half IS the hours the name cell says are beyond the bank. Printing
        // only the total left "17.00 h over the bank" beside "$2,985" with no
        // stated relationship — which reads as a second price for the same
        // hours. It is one price ($2,805) and one expense ($180).
        const split = unbilledSplit(c.unbilled, c.unbilledExpenses);
        return (
          <>
            {renderUnbilled(c)}
            {split && (
              <div
                className="num mt-0.5 text-caption font-normal leading-tight text-grey-500"
                title={UNBILLED_SPLIT_TITLE}
              >
                {split}
              </div>
            )}
            {c.unbilledPrepaid > 0 && (
              <div
                className="mt-0.5 text-caption font-normal leading-tight text-grey-500"
                title={PREPAID_TITLE}
              >
                <span className="num">{formatHours(c.unbilledPrepaidHours, { fixed: true })} h</span> prepaid
              </div>
            )}
          </>
        );
      },
    },
    {
      key: 'openAr',
      header: 'Open AR',
      align: 'right',
      sortable: true,
      sortValue: (c) => c.openAr,
      // Overdue is the urgent half of AR, so it is the one that survives to
      // 768px; the full open balance returns at 1280 where there is room.
      className: 'hidden xl:table-cell',
      render: (c) =>
        c.openAr > 0 ? (
          formatMoney(c.openAr, { whole: true })
        ) : (
          <span className="text-grey-400">—</span>
        ),
    },
    {
      key: 'overdueAr',
      header: 'Overdue',
      align: 'right',
      sortable: true,
      sortValue: (c) => c.overdueAr,
      className: 'hidden md:table-cell',
      render: (c) =>
        c.overdueAr > 0 ? (
          <span className="font-semibold text-primary-600">
            {formatMoney(c.overdueAr, { whole: true })}
          </span>
        ) : (
          <span className="text-grey-400">—</span>
        ),
    },
  ];

  /**
   * The rows on screen, in the order on screen, with the columns the screen
   * hides on small viewports included — a CSV is not responsive, and Hours and
   * Notes are real data even though the table only shows them in the name cell.
   * Money is whole dollars because that is what this list prints: a file that
   * says $226,312.50 under a screen that says $226,313 is the same bug the AR
   * hero used to have.
   */
  const exportCsv = () => {
    const rows = sortRows(clients, columns, sort);
    if (rows.length === 0) return;
    const whole = { whole: true } as const;
    downloadCsv(
      csvFilename({
        entity: 'clients',
        view: [search ? `search-${search}` : '', showArchived ? 'incl-archived' : '']
          .filter(Boolean)
          .join('-'),
      }),
      [
        [
          'Client',
          'Status',
          'Active agreements',
          'Agreements (all)',
          'Hours',
          'Revenue',
          `Invoiced ${YEAR}`,
          'Cost',
          'Internal cost',
          'Margin %',
          'Unbilled',
          'Unbilled since',
          'Prepaid support hours (uninvoiced)',
          'Prepaid support value (uninvoiced)',
          'Open AR',
          'Overdue AR',
          // The support bank, as data. The screen can only afford one line of
          // it under the client's name; a spreadsheet can sort on it.
          'Support hours left',
          'Support hours over',
          'Support status',
          'Support runs out',
          'Runs out because',
          'Notes',
        ],
        ...rows.map((c) => [
          c.name,
          c.isActive ? 'Active' : 'Archived',
          c.activeAgreements,
          c.totalAgreements,
          csvHours(c.hours),
          csvMoney(c.revenue, whole),
          csvMoney(c.ytdRevenue, whole),
          csvMoney(c.cost, whole),
          csvMoney(c.internalCost, whole),
          csvPercent(c.margin),
          csvMoney(c.unbilled, whole),
          c.unbilled > 0 ? (c.unbilledFrom ?? '') : '',
          csvHours(c.unbilledPrepaidHours),
          csvMoney(c.unbilledPrepaid, whole),
          csvMoney(c.openAr, whole),
          csvMoney(c.overdueAr, whole),
          c.support ? csvHours(c.support.balance) : '',
          c.support ? csvHours(c.support.overage) : '',
          c.support ? bankStatusLabel(c.support.status) : '',
          c.support?.runsOut ?? '',
          c.support?.runsOutReason ?? '',
          c.notes ?? '',
        ]),
        [],
        // The same two-line footing the table prints — client work and internal
        // overhead totalled separately, never blended into one margin.
        [
          `Total · ${totals.count} client${totals.count === 1 ? '' : 's'}`,
          '',
          '',
          '',
          csvHours(totals.hours),
          csvMoney(totals.revenue, whole),
          csvMoney(totals.ytdRevenue, whole),
          csvMoney(totals.cost, whole),
          csvMoney(totals.internalCost, whole),
          csvPercent(totals.margin),
          csvMoney(totals.unbilled, whole),
          '',
          csvHours(totals.unbilledPrepaidHours),
          csvMoney(totals.unbilledPrepaid, whole),
          csvMoney(totals.openAr, whole),
          csvMoney(totals.overdueAr, whole),
          csvHours(totals.supportBalance),
          csvHours(totals.supportOverage),
        ],
      ],
    );
  };

  const totalsRow = (
    <>
      <tr>
        <td className="px-3 py-2 text-caption uppercase tracking-wider text-grey-500">
          {totals.count} client{totals.count === 1 ? '' : 's'} ·{' '}
          <span className="normal-case tracking-normal">
            {formatHours(totals.hours, { fixed: true })} h
          </span>
          {/* The short qualifier the rollup keeps: this money blends three
              bases and is not the cash the P&L calls revenue. Six words. */}
          <span className="block font-normal normal-case tracking-normal text-grey-400">
            {REVENUE_BASIS_NOTE_SHORT}
            <InfoHint label="Revenue basis" width={320}>
              <RevenueBasisInfo />
              <p className="mt-2">{REVENUE_SCOPE_NOTE}</p>
            </InfoHint>
          </span>
        </td>
        <td className="hidden 2xl:table-cell" />
        <td className="num-cell hidden px-3 py-2 sm:table-cell">
          {formatMoney(totals.revenue, { whole: true })}
          <div
            className="text-caption font-normal leading-tight text-grey-500"
            title={YTD_TITLE}
          >
            {formatMoney(totals.ytdRevenue, { whole: true })} billed in {YEAR}
          </div>
        </td>
        <td className="num-cell hidden px-3 py-2 xl:table-cell">
          {formatMoney(totals.cost, { whole: true })}
        </td>
        <td className="num-cell px-3 py-2">
          <span className="inline-flex items-center gap-0.5">
            <MarginText margin={totals.margin} />
            {totals.internalCost > 0 && (
              <InfoHint label="Margin basis" align="end" width={300}>
                <p>
                  Margin on CLIENT work only. A further{' '}
                  <span className="num font-semibold text-secondary">
                    {formatMoney(totals.internalCost, { whole: true })}
                  </span>{' '}
                  of internal / non-billable cost is booked against these clients and is left out of
                  it — a blended margin that quietly absorbs non-billable cost reads as a business
                  doing worse than its client work is.
                </p>
              </InfoHint>
            )}
          </span>
        </td>
        <td className="num-cell hidden px-3 py-2 md:table-cell">
          <span className="inline-flex items-center gap-0.5">
            {totals.unbilled > 0 ? (
              formatMoney(totals.unbilled, { whole: true })
            ) : (
              <span className="text-grey-400">—</span>
            )}
            <InfoHint label="Unbilled" align="end" width={320}>
              <p>{UNBILLED_TITLE}</p>
              {totals.unbilledPrepaid > 0 && (
                <p className="mt-2">
                  A further{' '}
                  <span className="num font-semibold text-secondary">
                    {formatHours(totals.unbilledPrepaidHours, { fixed: true })} h ·{' '}
                    {formatMoney(totals.unbilledPrepaid, { whole: true })}
                  </span>{' '}
                  of uninvoiced support is already paid for out of prepaid blocks, so it is excluded
                  here — invoicing it would charge twice for the same hour.
                </p>
              )}
            </InfoHint>
          </span>
        </td>
        <td className="num-cell hidden px-3 py-2 xl:table-cell">
          {formatMoney(totals.openAr, { whole: true })}
        </td>
        <td className="num-cell hidden px-3 py-2 md:table-cell">
          {totals.overdueAr > 0 ? (
            <span className="text-primary-600">{formatMoney(totals.overdueAr, { whole: true })}</span>
          ) : (
            <span className="text-grey-400">—</span>
          )}
        </td>
      </tr>
      {/* ⚠ THREE PARAGRAPHS USED TO SIT HERE — the revenue basis, the prepaid
          netting and the internal-cost addendum, 101px of prose under a table
          (Jesse, 2026-08-13: *"don't need explanations on clients view"*). Each
          one now hangs off the figure it explains as an (i): the revenue basis
          on the Revenue header and this totals band, the prepaid split on the
          Unbilled total, the internal cost on Margin. Nothing was deleted, and
          the figures those sentences carried are inside the popovers. */}
    </>
  );

  return (
    <div>
      <PageHeader
        title="Clients"
        subtitle={
          isLoading
            ? 'Loading…'
            : // The headline the list exists to give: how many, what they have
              // billed this year, and how much delivered work is still sitting
              // uninvoiced. All three came out of the rows already on screen.
              [
                `${clients.length} ${showArchived ? '' : 'active '}client${clients.length === 1 ? '' : 's'}`,
                totals.ytdRevenue > 0
                  ? `${formatMoney(totals.ytdRevenue, { whole: true })} invoiced in ${YEAR}`
                  : null,
                totals.unbilled > 0
                  ? `${formatMoney(totals.unbilled, { whole: true })} unbilled`
                  : null,
                // Named here too: the headline used to read "$46,180 unbilled"
                // when $11,703 of it was support the clients had already
                // bought. The subtitle is where an owner forms the number they
                // then go and bill.
                totals.unbilledPrepaid > 0
                  ? `${formatMoney(totals.unbilledPrepaid, { whole: true })} of support already prepaid`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')
        }
        actions={
          <>
            {/* Header, like /agreements and /invoices — this page's header is not
                shared with a second tab, so it owns the export. */}
            <ExportCsvButton
              count={clients.length}
              noun="clients"
              size="md"
              onClick={exportCsv}
            />
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" aria-hidden />
              New client
            </Button>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput searches="clients" value={search} onChange={setSearch} className="w-full max-w-xs" />
        <ArchivedToggle
          noun="clients"
          active={showArchived}
          count={archivedCount}
          onChange={(next) => patch({ archived: next ? '1' : null })}
        />
      </div>

      <Card>
        {isLoading ? (
          <SkeletonTable rows={8} />
        ) : isError ? (
          <EmptyState
            icon={<AlertTriangle />}
            title="Couldn't load clients"
            description="Something went wrong talking to the server."
            action={
              <Button variant="outline" onClick={() => refetch()}>
                Try again
              </Button>
            }
          />
        ) : (
          <DataTable
            // Compact density: the revenue cell now carries two figures, and on
            // a triage list how many clients you can see IS the feature. It
            // also narrows the cell padding, which is what buys the eighth
            // column its room at 1280 (see the note on the agreements column).
            density="compact"
            columns={columns}
            data={clients}
            rowKey={(c) => c.id}
            rowHref={(c) => `/clients/${c.id}`}
            rowLabel={(c) => c.name}
            onRowClick={(c) => navigate(`/clients/${c.id}`)}
            sort={sort}
            onSortChange={(next) => patch({ sort: serializeSortParam(next) })}
            footer={clients.length > 0 ? totalsRow : undefined}
            empty={
              search ? (
                <EmptyState
                  icon={<Search />}
                  title="No clients match"
                  description={`Nothing found for “${search}”. Try a different search.`}
                  action={
                    <Button variant="outline" onClick={() => setSearch('')}>
                      Clear search
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={<Users />}
                  title="No clients yet"
                  description="Add your first client to start tracking agreements, time, and invoices."
                  action={
                    <Button onClick={() => setCreateOpen(true)}>
                      <Plus className="h-4 w-4" aria-hidden />
                      New client
                    </Button>
                  }
                />
              )
            }
          />
        )}
      </Card>

      <NewClientModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => navigate(`/clients/${id}`)}
      />
    </div>
  );
}

/**
 * The Unbilled cell's figure — the money, or the dash, or the unclickable
 * figure for an archived client. Extracted so the "N h prepaid" line that
 * explains what the bank already covered can sit under all three without
 * three copies of the link.
 */
function renderUnbilled(c: ClientListRow) {
  return c.unbilled <= 0 ? (
    <span className="text-grey-400">—</span>
  ) : c.isActive && c.unbilledFrom ? (
          // The figure IS the action. An alert with no way to act on it is the
          // step count a spreadsheet already beats; this opens the invoice
          // wizard already loaded on the client and the span that holds the
          // money — the same destination the client page's primary button uses.
          <Link
            to={unbilledWizardHref(c.id, c.unbilledFrom, c.name)}
            onClick={(e) => e.stopPropagation()}
            title={
              `Invoice ${formatMoney(c.unbilled, { whole: true })} of unbilled work for ${c.name} — delivered since ${formatDate(c.unbilledFrom)}` +
              (c.unbilledExpenses > 0
                ? `, including ${formatMoney(c.unbilledExpenses, { whole: true })} of billable expenses`
                : '') +
              (c.unbilledPrepaid > 0
                ? `. A further ${formatHours(c.unbilledPrepaidHours)} h (${formatMoney(c.unbilledPrepaid, { whole: true })}) of support in that span is already paid for out of their prepaid block — do not bill it again.`
                : '')
            }
            className="inline-flex items-center gap-1.5 rounded-md font-semibold text-secondary no-underline outline-none transition-colors hover:text-accent focus-visible:ring-2 focus-visible:ring-accent/50"
          >
            {formatMoney(c.unbilled, { whole: true })}
            <Receipt
              className="h-3.5 w-3.5 shrink-0 text-grey-300 transition-colors group-hover:text-accent"
              aria-hidden
            />
          </Link>
        ) : (
          // Archived clients are excluded from the invoice wizard's own query,
          // so the figure is reported and not offered.
          <span className="font-semibold text-grey-500" title={UNBILLED_TITLE}>
            {formatMoney(c.unbilled, { whole: true })}
          </span>
        );
}
